(() => {
  const GAP = 12;
  const SPEED = 42;
  const MOBILE_MQ = '(max-width: 749px)';
  const AXIS_LOCK = 8;
  const CLICK_SUPPRESS = 8;
  const RESUME_MS = 900;

  const sliders = [];
  let lastTime = 0;
  let raf = 0;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => window.matchMedia(MOBILE_MQ).matches;

  const loopWidth = (slider) => {
    const card = slider.track.querySelector('.collection-slider-card');
    if (!card || !slider.count) return 0;
    return slider.count * (card.getBoundingClientRect().width + GAP);
  };

  const wrapOffset = (slider) => {
    const width = loopWidth(slider);
    if (!width) return 0;
    return ((slider.offset % width) + width) % width;
  };

  const applySlider = (slider) => {
    slider.track.style.transform = `translate3d(${-wrapOffset(slider)}px, 0, 0)`;
  };

  const apply = () => {
    sliders.forEach(applySlider);
  };

  const tick = (now) => {
    if (!lastTime) lastTime = now;
    const dt = Math.min(48, now - lastTime);
    lastTime = now;
    if (!reduced()) {
      sliders.forEach((slider) => {
        if (slider.paused) return;
        slider.offset += (SPEED * dt) / 1000;
      });
    }
    apply();
    raf = requestAnimationFrame(tick);
  };

  const bindDrag = (slider) => {
    const viewport = slider.root.querySelector('.collection-slider-viewport');
    if (!viewport) return;

    let session = null;
    let resumeTimer = 0;
    let suppressClick = false;

    const clearResume = () => {
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = 0;
    };

    const scheduleResume = () => {
      clearResume();
      resumeTimer = window.setTimeout(() => {
        slider.paused = false;
        resumeTimer = 0;
      }, RESUME_MS);
    };

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (!isMobile()) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        suppressClick = false;
        session = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          origin: slider.offset,
          axis: null,
          moved: false,
        };
      },
      { passive: true }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (!session || event.pointerId !== session.id) return;
        const dx = event.clientX - session.x;
        const dy = event.clientY - session.y;

        if (!session.axis) {
          if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
          session.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (session.axis === 'x') {
            clearResume();
            slider.paused = true;
            try {
              viewport.setPointerCapture(event.pointerId);
            } catch (_) {}
          }
        }

        if (session.axis !== 'x') return;
        if (Math.abs(dx) >= CLICK_SUPPRESS) session.moved = true;
        slider.offset = session.origin - dx;
        applySlider(slider);
      },
      { passive: true }
    );

    const end = (event) => {
      if (!session || (event && event.pointerId !== session.id)) return;
      if (session.moved) suppressClick = true;
      const wasHorizontal = session.axis === 'x';
      session = null;
      if (wasHorizontal) scheduleResume();
    };

    viewport.addEventListener('pointerup', end, { passive: true });
    viewport.addEventListener('pointercancel', end, { passive: true });

    viewport.addEventListener(
      'touchmove',
      (event) => {
        if (session && session.axis === 'x' && event.cancelable) {
          event.preventDefault();
        }
      },
      { passive: false }
    );

    viewport.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      },
      true
    );
  };

  const bind = (root) => {
    if (root.dataset.csBound === 'true') return;
    root.dataset.csBound = 'true';

    const track = root.querySelector('.collection-slider-track');
    const prev = root.querySelector('.collection-slider-btn--prev');
    const next = root.querySelector('.collection-slider-btn--next');
    if (!track) return;

    const originals = [...track.children];
    if (!originals.length) return;

    originals.forEach((card) => track.appendChild(card.cloneNode(true)));
    originals.forEach((card) => track.appendChild(card.cloneNode(true)));

    const slider = {
      root,
      track,
      count: originals.length,
      offset: 0,
      paused: false,
    };
    sliders.push(slider);
    bindDrag(slider);

    const nudge = (dir) => {
      const width = loopWidth(slider);
      if (!width) return;
      slider.offset += dir * (width / slider.count);
      applySlider(slider);
    };

    prev?.addEventListener('click', (event) => {
      event.preventDefault();
      nudge(-1);
    });
    next?.addEventListener('click', (event) => {
      event.preventDefault();
      nudge(1);
    });
  };

  const init = () => {
    document.querySelectorAll('[data-collection-slider]').forEach(bind);
    apply();
    if (!raf) raf = requestAnimationFrame(tick);
  };

  window.addEventListener('resize', apply);
  init();
  document.addEventListener('shopify:section:load', init);
})();
